import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaBoxes, FaEraser } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import MapeamentoGeralEstoqueService from './mapeamentoGeralEstoqueService';
import ResultadoTabela from './ResultadoTabela';

// W-0617 — Mapeamento Geral Estoque (GES > Gerencial > Estoque)
// Fonte: ges/UEstoque.pas (TFestoque) + UqrEstoque.pas. Relatório READ-ONLY, server-side.
// Filtros: multi-GE (TFGerencial[5]) + moeda de referência (Lmo, default 11) +
// checkbox "Mostra Custo Atualizado" (Checkcusto, default marcado).

const MOEDA_PADRAO = 11; // UEstoque.pas:261 — moeda.Locate('Codigo_ativo','11').

const extrairMensagemErro = (err, fallback) =>
  (err && (err.message || err.Mensagem)) || fallback;

const MapeamentoGeralEstoque = () => {
  const [moedas, setMoedas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [carregandoListas, setCarregandoListas] = useState(true);

  const [codigoMoeda, setCodigoMoeda] = useState(MOEDA_PADRAO);
  const [gesSelecionados, setGesSelecionados] = useState([]);
  const [mostraCusto, setMostraCusto] = useState(true); // Checkcusto.Checked = true

  const [resultado, setResultado] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let ativo = true;
    setCarregandoListas(true);
    Promise.all([
      MapeamentoGeralEstoqueService.listarMoedas(),
      MapeamentoGeralEstoqueService.listarGruposEmpresa(),
    ])
      .then(([resMoedas, resGrupos]) => {
        if (!ativo) return;
        setMoedas(Array.isArray(resMoedas) ? resMoedas : []);
        setGrupos(Array.isArray(resGrupos) ? resGrupos : []);
      })
      .catch((err) => {
        if (!ativo) return;
        const msg = extrairMensagemErro(
          err,
          'Erro ao carregar moedas e grupos de empresa.'
        );
        setErro(msg);
        toast.error(msg);
      })
      .then(() => {
        if (ativo) setCarregandoListas(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const opcoesMoeda = moedas.map((m) => ({
    value: m.codigo_ativo,
    label: `${m.codigo_ativo} - ${m.nome}`,
  }));
  const opcoesGE = grupos.map((g) => ({
    value: g.codigo_grupo_empresa,
    label: `${g.codigo_grupo_empresa} - ${g.apelido}`,
  }));

  const moedaSelecionada =
    opcoesMoeda.find((o) => o.value === codigoMoeda) || null;
  const gesSelecionadosOpts = opcoesGE.filter((o) =>
    gesSelecionados.includes(o.value)
  );

  const handleLimpar = () => {
    setCodigoMoeda(MOEDA_PADRAO);
    setGesSelecionados([]);
    setMostraCusto(true);
    setResultado(null);
    setErro('');
  };

  const handleGerar = async () => {
    setErro('');
    // RN-1 (UEstoque.pas:75): pelo menos 1 GE.
    if (!gesSelecionados.length) {
      const msg = 'Nenhum grupo de empresas selecionado.';
      setErro(msg);
      toast.warn(msg);
      return;
    }

    setGerando(true);
    setResultado(null);
    try {
      const res = await MapeamentoGeralEstoqueService.gerar({
        codigosGrupoEmpresa: gesSelecionados,
        codigoMoeda,
        mostraCusto,
      });
      setResultado(res || { linhas: [] });
    } catch (err) {
      const msg = extrairMensagemErro(err, 'Não foi possível gerar o relatório.');
      setErro(msg);
      toast.error(msg);
    } finally {
      setGerando(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <Header title="Mapeamento Geral Estoque" />

      <Card className="mt-3">
        <Card.Body>
          {carregandoListas ? (
            <Loading label="Carregando filtros..." />
          ) : (
            <Row>
              {/* Coluna de filtros */}
              <Col xs={12} md={5} lg={4}>
                <Form.Group>
                  <Form.Label>Moeda referência</Form.Label>
                  <Select
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    options={opcoesMoeda}
                    value={moedaSelecionada}
                    onChange={(opt) =>
                      setCodigoMoeda(opt ? opt.value : MOEDA_PADRAO)
                    }
                    placeholder="Selecione a moeda..."
                    noOptionsMessage={() => 'Nenhuma moeda disponível'}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="chk-mostra-custo"
                    label="Mostra Custo Atualizado"
                    checked={mostraCusto}
                    onChange={(e) => setMostraCusto(e.target.checked)}
                  />
                </Form.Group>

                <div className="d-flex">
                  <Button
                    variant="primary"
                    onClick={handleGerar}
                    disabled={gerando}
                    className="mr-2"
                  >
                    <FaBoxes className="mr-2" />
                    {gerando ? 'Gerando...' : 'Gerar'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleLimpar}
                    disabled={gerando}
                  >
                    <FaEraser className="mr-2" />
                    Limpar
                  </Button>
                </div>
              </Col>

              {/* Coluna do multi-GE picker */}
              <Col xs={12} md={7} lg={8}>
                <Form.Group>
                  <Form.Label>
                    Grupos de Empresa <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    isMulti
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    options={opcoesGE}
                    value={gesSelecionadosOpts}
                    onChange={(opts) =>
                      setGesSelecionados(opts ? opts.map((o) => o.value) : [])
                    }
                    placeholder="Selecione um ou mais GEs..."
                    noOptionsMessage={() => 'Nenhum GE disponível'}
                    closeMenuOnSelect={false}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {gerando && <Loading label="Gerando relatório..." />}

      {!gerando && erro && (
        <Alert variant="warning" className="mt-3">
          {erro}
        </Alert>
      )}

      {!gerando && resultado && <ResultadoTabela resultado={resultado} />}
    </Container>
  );
};

export default MapeamentoGeralEstoque;
